// Web Push helpers (browser side). All functions are safe to call on
// unsupported environments (older iOS, in-app browsers, etc.) — they return
// neutral values rather than throwing.

import { apiCallJson, ApiError } from './api';

/** True if the runtime can register a Service Worker + subscribe to push. */
export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** True if the app is running as an installed PWA (standalone display mode). */
export function isPwaInstalled() {
  if (typeof window === 'undefined') return false;
  // iOS Safari uses the legacy navigator.standalone flag.
  if (window.navigator && 'standalone' in window.navigator && window.navigator.standalone) return true;
  // Standard PWA detection.
  return window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
}

/** Detect iOS / iPadOS regardless of which browser is running. */
export function isIos() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/** Detect iOS Safari (where push requires installed PWA + iOS ≥ 16.4). */
export function isIosSafari() {
  if (!isIos()) return false;
  // Exclude Chrome/Firefox on iOS (they all UA-tag with "Safari" but include CriOS/FxiOS).
  return !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
}

/**
 * Web Push only works inside the installed PWA on iOS. On every other platform
 * (Android, desktop Chrome/Edge/Firefox, macOS Safari) it works straight from
 * the browser — installing the PWA is a nice-to-have but not a prerequisite.
 */
export function pushRequiresPwaInstall() {
  return isIos();
}

/** Current browser permission for Notifications ('granted' | 'denied' | 'default' | 'unsupported'). */
export function getNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/** Register the SW. Returns the registration or null if SW isn't supported. */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch (err) {
    console.warn('SW registration failed', err);
    return null;
  }
}

// VAPID public keys come from the server as URL-safe base64. The browser's
// pushManager.subscribe() wants a raw Uint8Array of the key bytes.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

/** True if an existing browser subscription was created with `keyBytes`. */
function subscriptionMatchesKey(subscription, keyBytes) {
  try {
    const existing = subscription.options && subscription.options.applicationServerKey;
    if (!existing) return false; // unknown → treat as mismatch, re-subscribe fresh
    const a = new Uint8Array(existing);
    if (a.length !== keyBytes.length) return false;
    for (let i = 0; i < a.length; i += 1) if (a[i] !== keyBytes[i]) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Subscribe this device to push notifications. Handles the full flow:
 *  1. Register SW (if not already)
 *  2. Ask Notification permission
 *  3. Fetch the server's VAPID public key
 *  4. pushManager.subscribe(...)
 *  5. POST the subscription to the backend
 *
 * @returns {Promise<{subscription:object}>} server-returned subscription row
 * @throws {Error} with a user-friendly message on any failure
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Les notifications push ne sont pas supportées sur cet appareil/navigateur.');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'Permission refusée. Activez les notifications dans les paramètres du navigateur/téléphone.'
      : 'Permission non accordée.');
  }
  const registration = (await navigator.serviceWorker.getRegistration())
    || await registerServiceWorker();
  if (!registration) throw new Error('Service worker indisponible.');
  await navigator.serviceWorker.ready;

  // Fetch the public key from the server (env-driven so we can rotate keys).
  let publicKey;
  try {
    const keyData = await apiCallJson('/api/push/vapid-public-key', { method: 'GET' });
    publicKey = keyData.publicKey;
  } catch (err) {
    throw new Error(err instanceof ApiError ? (err.message || 'Impossible de récupérer la clé VAPID.') : 'Impossible de récupérer la clé VAPID.');
  }
  if (!publicKey) throw new Error('Clé VAPID vide côté serveur.');

  // Re-use existing subscription if present (avoids unsubscribing/resubscribing
  // every visit) — but ONLY if it was created with the current VAPID key. If
  // the server rotated keys, a stale-key subscription would be reused forever
  // and every push would be rejected by the push service. Compare the bytes
  // and re-subscribe on mismatch.
  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  let pushSubscription = await registration.pushManager.getSubscription();
  if (pushSubscription && !subscriptionMatchesKey(pushSubscription, applicationServerKey)) {
    try { await pushSubscription.unsubscribe(); } catch { /* ignore */ }
    pushSubscription = null;
  }
  if (!pushSubscription) {
    pushSubscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  try {
    const data = await apiCallJson('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        subscription: pushSubscription.toJSON(),
        userAgent: navigator.userAgent,
      }),
    });
    return { subscription: data.subscription };
  } catch (err) {
    throw new Error(err instanceof ApiError ? (err.message || 'L\'enregistrement de la subscription a échoué.') : 'L\'enregistrement de la subscription a échoué.');
  }
}

/**
 * Remove a server subscription row. If alsoUnsubscribeBrowser is true (only
 * meaningful when deleting THIS device's row), also tear down the browser-side
 * push subscription so no more events fire locally. Idempotent.
 */
export async function unsubscribeFromPush(subscriptionId, alsoUnsubscribeBrowser = true) {
  if (subscriptionId != null) {
    try {
      await apiCallJson(`/api/push/subscriptions/${subscriptionId}`, { method: 'DELETE' });
    } catch { /* ignore */ }
  }
  if (alsoUnsubscribeBrowser) {
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) await sub.unsubscribe();
        }
      }
    } catch { /* ignore */ }
  }
  return true;
}

/** List the current user's registered devices. */
export async function listMySubscriptions() {
  try {
    const data = await apiCallJson('/api/push/subscriptions', { method: 'GET' });
    return Array.isArray(data.subscriptions) ? data.subscriptions : [];
  } catch {
    return [];
  }
}

/** Set/clear the friendly label on a device. */
export async function renameSubscription(subscriptionId, deviceLabel) {
  try {
    const data = await apiCallJson(`/api/push/subscriptions/${subscriptionId}`, {
      method: 'PUT',
      body: JSON.stringify({ deviceLabel }),
    });
    return data.subscription;
  } catch (err) {
    throw new Error(err instanceof ApiError ? (err.message || 'Renommage échoué') : 'Renommage échoué');
  }
}

/** Guess a friendly device label from a UA string ("iPhone Safari", "Chrome on Android"…). */
export function guessDeviceLabel(userAgent) {
  if (!userAgent) return 'Appareil inconnu';
  const ua = userAgent;
  let os = 'Web';
  if (/iPhone/.test(ua)) os = 'iPhone';
  else if (/iPad/.test(ua)) os = 'iPad';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Mac OS X/.test(ua)) os = 'Mac';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Linux/.test(ua)) os = 'Linux';
  let browser = 'Navigateur';
  if (/EdgiOS|Edg\//.test(ua)) browser = 'Edge';
  else if (/CriOS|Chrome\//.test(ua)) browser = 'Chrome';
  else if (/FxiOS|Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  return `${os} — ${browser}`;
}
